import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../ui/Modal';
import { Button } from '../../ui/Button';
import BirthDateInput from '../../ui/BirthDateInput';
import { toast } from '../../ui/Toast';
import { agenciesApi, type AgencyClient } from '../../../api/agencies.api';

const EMAIL = /^\S+@\S+\.\S+$/;

interface Props {
  agencyId: string;
  /** Editing this one; a new client when absent. */
  client?: AgencyClient;
  /** Prefilled name, e.g. what was typed in the picker's search. */
  initialName?: string;
  onClose: () => void;
  onSaved: (client: AgencyClient) => void;
}

/** ADR-0037: one client of the agency's book. Only the name is required — a child has no email. */
export default function ClientFormModal({ agencyId, client, initialName = '', onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(client?.name ?? initialName);
  const [email, setEmail] = useState(client?.email ?? '');
  const [phone, setPhone] = useState(client?.phone ?? '');
  const [birthDate, setBirthDate] = useState(client?.birthDate ?? '');
  const [notes, setNotes] = useState(client?.notes ?? '');
  const [saving, setSaving] = useState(false);

  const emailValid = !email.trim() || EMAIL.test(email.trim());
  const valid = !!name.trim() && emailValid;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    const data = {
      name: name.trim(),
      email: email.trim().toLowerCase() || null,
      phone: phone.trim() || null,
      birthDate: birthDate || null,
      notes: notes.trim() || null,
    };
    try {
      const saved = client
        ? await agenciesApi.updateClient(agencyId, client.id, data)
        : await agenciesApi.createClient(agencyId, data);
      onSaved(saved);
    } catch {
      toast(t('agency.clients.saveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={t(client ? 'agency.clients.edit' : 'agency.clients.new')}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label" htmlFor="client-name">{t('agency.clients.form.name')}</label>
          <input id="client-name" className="input" maxLength={60} autoFocus value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="client-email">{t('agency.clients.form.email')}</label>
            <input id="client-email" className="input" type="email" maxLength={255} value={email} onChange={e => setEmail(e.target.value)} />
            {!emailValid && <p className="text-[12px] text-red-500 mt-1">{t('agency.clients.form.emailInvalid')}</p>}
          </div>
          <div>
            <label className="label" htmlFor="client-phone">{t('agency.clients.form.phone')}</label>
            <input id="client-phone" className="input" type="tel" maxLength={40} value={phone} onChange={e => setPhone(e.target.value)} />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="client-birth">{t('agency.clients.form.birthDate')}</label>
          <BirthDateInput id="client-birth" value={birthDate} onChange={setBirthDate} />
        </div>
        <div>
          <label className="label" htmlFor="client-notes">{t('agency.clients.form.notes')}</label>
          <textarea id="client-notes" className="input min-h-[72px]" maxLength={2000} value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <p className="text-[12px] text-slate-400">{t('agency.clients.form.emailHint')}</p>
        <Button type="submit" variant="cta" className="w-full" loading={saving} disabled={!valid}>
          {t('common.save')}
        </Button>
      </form>
    </Modal>
  );
}
